import React, { useRef, useState, useEffect, useCallback } from 'react';
import clamp from 'lodash/clamp';
import type { Position } from '@atlassian/jira-drag-observer';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { MINIMUM_LIST_WIDTH, LIST_RESIZE_DRAG_AND_DROP } from '../../../../../common/constants';
import type {
	OnSetListWidth as OnResize,
	DndLongTaskMetricComponent,
} from '../../../../../common/types';
import ResizeHandler from '../resize-handler';
import { useMaxWidth } from '../use-max-width';

type Props = {
	width: number;
	DndLongTaskMetric: DndLongTaskMetricComponent | undefined;
	onResize: OnResize;
	onResizeEnd: OnResize;
};

const Resizer = ({ width, DndLongTaskMetric, onResize, onResizeEnd }: Props) => {
	const initialWidth = useRef<number>(width);
	const [isDragging, setIsDragging] = useState<boolean>(false);
	const maxWidth = useMaxWidth();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		width > maxWidth && onResize(maxWidth);
	}, [width, maxWidth, onResize]);

	const onDragStart = useCallback(() => {
		initialWidth.current = width;
		setIsDragging(true);
	}, [width]);

	const onDrag = useCallback(
		(from: Position, to: Position, start: Position) => {
			const newWidth = Math.round(initialWidth.current + to.x - start.x);
			const saneWidth = clamp(newWidth, MINIMUM_LIST_WIDTH, maxWidth);

			onResize(saneWidth);
		},
		[maxWidth, onResize],
	);

	const onDragEnd = useCallback(
		(from: Position, to: Position) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'resized',
				actionSubject: 'list',
			});

			fireUIAnalytics(analyticsEvent, 'resizedList', { didExpand: to.x > from.x });
			setIsDragging(false);
			onResizeEnd(initialWidth.current);
		},
		[createAnalyticsEvent, onResizeEnd],
	);

	return (
		<>
			<ResizeHandler onDragStart={onDragStart} onDrag={onDrag} onDragEnd={onDragEnd} />
			{DndLongTaskMetric !== undefined && (
				<DndLongTaskMetric isDragging={isDragging} metricType={LIST_RESIZE_DRAG_AND_DROP} />
			)}
		</>
	);
};

export default Resizer;
